import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { PortalShell, usePortalAccount } from "./PortalShell";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

const CLASSES = ["Class 9", "Class 10", "Class 11", "Class 12", "Dropper"];
const EXAMS = ["School", "NEET", "JEE Main", "JEE Advanced", "Other"];
const STREAMS = ["", "PCM", "PCB", "Other"];
const SUBJECTS = ["Physics", "Chemistry", "Mathematics", "Biology"];

function PortalProfileInner() {
  const account = usePortalAccount();
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ name: account.name, mobile: account.mobile || "", current_class: account.current_class, board: account.board || "",
              target_exam: account.target_exam, stream: account.stream || "", subjects: account.subjects || [],
              target_year: account.target_year || "", goals: account.goals || "" });
  }, [account]);

  if (!form) return null;
  const incomplete = account.profile_complete === false || location.state?.completeProfile;
  const update = (key, value) => { setForm((c) => ({ ...c, [key]: value })); setSaved(false); };
  const toggleSubject = (s) => update("subjects", form.subjects.includes(s) ? form.subjects.filter((x) => x !== s) : [...form.subjects, s]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await portalApi.patch("/portal/profile", form);
      setSaved(true);
    } catch (err) { setError(portalErr(err)); } finally { setSaving(false); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 640, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">MY PROFILE</div>
      <h1 className="tp-h2" style={{ fontSize: 30 }} data-testid="portal-profile-title">{account.name}</h1>
      {incomplete && (
        <div className="tp-panel" style={{ marginTop: 16, border: "1px solid #f59e0b", background: "#3a2a12" }} data-testid="portal-complete-banner">
          <b style={{ color: "#fbbf24" }}>Almost done!</b>
          <p className="tp-muted" style={{ margin: "6px 0 0" }}>Add your mobile number, class and target exam below so we can personalise your quizzes, streaks and rewards.</p>
        </div>
      )}
      <div className="tp-panel" style={{ marginTop: 16 }}>
        <div className="tp-detail-grid" data-testid="portal-profile-info">
          <div>STUDENT ID<b data-testid="portal-profile-id">{account.student_id}</b></div>
          <div>EMAIL<b>{account.email}</b></div>
          <div>SIGN-IN METHOD<b>{account.auth_provider === "google" ? "Google" : account.auth_provider === "email_otp" ? "Email OTP" : "Password"}</b></div>
          <div>MEMBER SINCE<b>{new Date(account.created_at * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</b></div>
        </div>
        <p className="tp-note">Email can&apos;t be changed — WhatsApp us if you need to update it.</p>
      </div>
      <div className="tp-panel" style={{ marginTop: 16 }}>
        <h2 className="tp-h2" style={{ fontSize: 20 }}>Edit profile</h2>
        {error && <p className="tp-error" role="alert" data-testid="portal-profile-error">{error}</p>}
        {saved && <p className="tp-pill ok" data-testid="portal-profile-saved"><Check size={14} /> Profile saved</p>}
        <form onSubmit={save} data-testid="portal-profile-form">
          <label className="tp-label">FULL NAME<input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="portal-edit-name" /></label>
          <label className="tp-label">MOBILE<input required value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder="10-digit number" inputMode="numeric" data-testid="portal-edit-mobile" /></label>
          <div className="tp-grid-2">
            <label className="tp-label">CLASS<select required value={form.current_class} onChange={(e) => update("current_class", e.target.value)} data-testid="portal-edit-class"><option value="">Select class</option>{CLASSES.map((c) => <option key={c}>{c}</option>)}</select></label>
            <label className="tp-label">TARGET EXAM<select required value={form.target_exam} onChange={(e) => update("target_exam", e.target.value)} data-testid="portal-edit-exam"><option value="">Select exam</option>{EXAMS.map((x) => <option key={x}>{x}</option>)}</select></label>
          </div>
          <div className="tp-grid-2">
            <label className="tp-label">BOARD<input value={form.board} onChange={(e) => update("board", e.target.value)} placeholder="CBSE / ICSE / State…" data-testid="portal-edit-board" /></label>
            <label className="tp-label">TARGET YEAR<input value={form.target_year} onChange={(e) => update("target_year", e.target.value)} inputMode="numeric" data-testid="portal-edit-year" /></label>
          </div>
          <label className="tp-label">STREAM<select value={form.stream} onChange={(e) => update("stream", e.target.value)} data-testid="portal-edit-stream">{STREAMS.map((s) => <option key={s} value={s}>{s || "Select stream"}</option>)}</select></label>
          <div style={{ marginBottom: 14 }}>
            <span className="tp-label" style={{ marginBottom: 8 }}>SUBJECTS YOU STUDY</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SUBJECTS.map((s) => <button type="button" key={s} className={form.subjects.includes(s) ? "tp-slot selected" : "tp-slot"} onClick={() => toggleSubject(s)} data-testid={`portal-edit-subject-${s.toLowerCase()}`}>{s}</button>)}
            </div>
          </div>
          <label className="tp-label">LEARNING GOALS / WEAK TOPICS<textarea rows={3} value={form.goals} onChange={(e) => update("goals", e.target.value)} data-testid="portal-edit-goals" /></label>
          <button className="tp-btn" style={{ width: "100%" }} disabled={saving} type="submit" data-testid="portal-profile-save">{saving ? "Saving…" : "SAVE PROFILE"}</button>
        </form>
      </div>
    </div>
  );
}

export default function PortalProfile() {
  return <PortalShell><PortalProfileInner /></PortalShell>;
}
